import { isModalRoutePath, resolveModalHref } from '../utils/modal-routes';

import { useCallback, useMemo } from 'react';
import type { NavigateOptions } from 'react-router';
import { useLocation, useNavigate } from 'react-router';

// ----------------------------------------------------------------------

export interface RouterPushOptions extends NavigateOptions {
  /** Additional state to pass along with the navigation */
  state?: Record<string, unknown>;
  /** Set to false to force a standalone page navigation for a modal-capable path. */
  modal?: boolean;
}

export function useRouter() {
  const navigate = useNavigate();
  const location = useLocation();

  const navigateTo = useCallback(
    (href: string, options?: RouterPushOptions, replace = false) => {
      const isModalRoute = options?.modal !== false && isModalRoutePath(href);
      const targetHref = isModalRoute ? resolveModalHref(href, location) : href;
      const { modal: _modal, ...navigateOptions } = options ?? {};

      navigate(targetHref, {
        ...navigateOptions,
        replace,
        preventScrollReset: navigateOptions.preventScrollReset ?? isModalRoute,
      });
    },
    [location, navigate],
  );

  const router = useMemo(
    () => ({
      back: () => navigate(-1),
      forward: () => navigate(1),
      reload: () => window.location.reload(),
      /**
       * Navigate to a path.
       * For modal routes, automatically stores the target in the `modal` search param.
       */
      push: (href: string, options?: RouterPushOptions) => {
        navigateTo(href, options);
      },
      replace: (href: string, options?: RouterPushOptions) => {
        navigateTo(href, options, true);
      },
    }),
    [navigate, navigateTo],
  );

  return router;
}
